import {
	getImproveAPI,
	AdminFeedResponse,
	GetRecentActivityResponse,
	GetActiveCoursesResponse,
	GetUserActivePlaylistsResponse,
	GetFinishedCoursesResponse,
	GetRecommendedCoursesResponse,
	BulkUnenrollCoursesResponse,
	UsersControllerGetFeedParams,
	UsersControllerGetRecentActivityParams,
	GetUserByIdResponse,
	ListUsersResponse,
	UserListDto,
	UsersControllerListParams
} from "../gen";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";

/**
 * Query key constants for admin user cache management
 */
export const USERS_QUERY_KEYS = {
	USER_FEED: "users:feed",
	USER_OVERDUE_LESSONS: "users:overdueLessons",
	USER_RECENT_ACTIVITY: "users:recentActivity",
	USER_ACTIVE_COURSES: "users:activeCourses",
	USER_ACTIVE_PLAYLISTS: "users:activePlaylists",
	USER_FINISHED_COURSES: "users:finishedCourses",
	USER_RECOMMENDED_COURSES: "users:recommendedCourses",
	USER_BY_ID: "users:byId",
	LIST: "users:list"
} as const;

/** Re-export generated types for list users */
export type { ListUsersResponse, UserListDto, UsersControllerListParams };

/** Alias for backward compatibility */
export type ListUsersParams = UsersControllerListParams;

/**
 * List users in the admin's cohort (server-side pagination, sort, filter).
 * Uses generated client. cohortId is determined by the backend from AuthUser.
 */
export const useListUsers = (params: UsersControllerListParams = {}) => {
	const axiosConfig = useImproveAxios();
	const { usersControllerList } = getImproveAPI();

	return useQuery<ListUsersResponse>({
		queryKey: [USERS_QUERY_KEYS.LIST, params],
		queryFn: async () => {
			const response = await usersControllerList(params, {
				...axiosConfig,
				paramsSerializer: {
					serialize: (requestParams: Record<string, unknown>) => {
						if (!requestParams) return "";
						const { filters, ...rest } = requestParams;
						const pairs: string[] = [];
						for (const [key, value] of Object.entries(rest)) {
							if (value !== undefined && value !== null) {
								pairs.push(
									`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
								);
							}
						}
						if (filters !== undefined && filters !== null) {
							pairs.push(
								`filters=${encodeURIComponent(JSON.stringify(filters))}`
							);
						}
						return pairs.join("&");
					}
				}
			});
			return response.data;
		},
		staleTime: 30 * 1000 // 30 seconds
	});
};

export const useGetUser = (userId: number) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetUserById} = getImproveAPI();
	if (!userId) {
		throw new Error("User ID is required to get user");
	}

	return useQuery<GetUserByIdResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_BY_ID, userId],
		queryFn: async () => {
			const response = await usersControllerGetUserById(userId, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 1 * 60 * 1000 // 1 minute
	});
};

/**
 * Admin: Get a specific user's training feed
 *
 * Returns the full feed (no daily limit) for admin viewing.
 *
 * @param userId - Target user ID
 * @param params - Optional query params (timezone)
 */
export const useGetUserFeed = (userId: number, params?: UsersControllerGetFeedParams) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetFeed} = getImproveAPI();

	return useQuery<AdminFeedResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_FEED, userId, params],
		queryFn: async () => {
			const response = await usersControllerGetFeed(userId, params, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 2 * 60 * 1000 // 2 minutes - admin views may need fresher data
	});
};

/**
 * Admin: Get a specific user's overdue lessons
 *
 * Returns all overdue lessons for admin viewing.
 *
 * @param userId - Target user ID
 * @param params - Query params (date and optional timezone)
 */
export const useGetUserOverdueLessons = (userId: number, params: { date: string; timezone?: string }) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetOverdueLessons} = getImproveAPI();

	return useQuery<AdminFeedResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_OVERDUE_LESSONS, userId, params],
		queryFn: async () => {
			const response = await usersControllerGetOverdueLessons(userId, params, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 2 * 60 * 1000 // 2 minutes - admin views may need fresher data
	});
};

/**
 * Admin: Get a specific user's recent activity (completed lessons)
 *
 * @param userId - Target user ID
 * @param params - Pagination params
 */
export const useGetUserRecentActivity = (
	userId: number,
	params?: UsersControllerGetRecentActivityParams
) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetRecentActivity} = getImproveAPI();

	return useQuery<GetRecentActivityResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_RECENT_ACTIVITY, userId, params],
		queryFn: async () => {
			const response = await usersControllerGetRecentActivity(userId, params, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 1 * 60 * 1000 // 1 minute
	});
};

/**
 * Admin: Get a specific user's active courses
 *
 * @param userId - Target user ID
 */
export const useGetUserActiveCourses = (userId: number) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetActiveCourses} = getImproveAPI();

	return useQuery<GetActiveCoursesResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId],
		queryFn: async () => {
			const response = await usersControllerGetActiveCourses(userId, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 2 * 60 * 1000 // 2 minutes
	});
};

/**
 * Admin: Active playlist enrollments for a user (playlist shell rows still in progress).
 */
export const useGetUserActivePlaylists = (userId: number) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetActivePlaylists} = getImproveAPI();

	return useQuery<GetUserActivePlaylistsResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_PLAYLISTS, userId],
		queryFn: async () => {
			const response = await usersControllerGetActivePlaylists(userId, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 2 * 60 * 1000
	});
};

/**
 * Admin: Get a specific user's finished courses
 *
 * @param userId - Target user ID
 */
export const useGetUserFinishedCourses = (userId: number) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetFinishedCourses} = getImproveAPI();

	return useQuery<GetFinishedCoursesResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_FINISHED_COURSES, userId],
		queryFn: async () => {
			const response = await usersControllerGetFinishedCourses(userId, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 5 * 60 * 1000 // 5 minutes - finished courses change less often
	});
};

/**
 * Admin: Get a specific user's recommended courses (training pool)
 *
 * @param userId - Target user ID
 */
export const useGetUserRecommendedCourses = (userId: number) => {
	const axiosConfig = useImproveAxios();
	const {usersControllerGetRecommendedCourses} = getImproveAPI();

	return useQuery<GetRecommendedCoursesResponse>({
		queryKey: [USERS_QUERY_KEYS.USER_RECOMMENDED_COURSES, userId],
		queryFn: async () => {
			const response = await usersControllerGetRecommendedCourses(userId, axiosConfig);
			return response.data;
		},
		enabled: !!userId,
		staleTime: 5 * 60 * 1000 // 5 minutes
	});
};

/**
 * Admin: Bulk unenroll a user from multiple courses
 *
 * On success, invalidates the user's active courses cache.
 *
 * @example
 * ```typescript
 * const { mutate: bulkUnenroll, isPending } = useBulkUnenrollUserFromCourses(userId);
 *
 * bulkUnenroll([1, 2, 3], {
 *   onSuccess: (result) => {
 *     toast.success(`Unenrolled from ${result.unenrolledCourseIds.length} courses`);
 *   }
 * });
 * ```
 */
export const useBulkUnenrollUserFromCourses = (userId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {usersControllerBulkUnenrollFromCourses} = getImproveAPI();

	return useMutation<BulkUnenrollCoursesResponse, Error, number[]>({
		mutationFn: async (courseIds: number[]) => {
			const response = await usersControllerBulkUnenrollFromCourses(
				userId,
				{courseIds},
				axiosConfig
			);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate user's active courses cache
			queryClient.invalidateQueries({
				queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId]
			});
			// Also invalidate feed as it may have changed
			queryClient.invalidateQueries({
				queryKey: [USERS_QUERY_KEYS.USER_FEED, userId]
			});
		}
	});
};

